/**
 * Environment configuration. Reads `import.meta.env` (Vite's build-time-replaced
 * env), normalizes defaults, and exposes them as a typed `env` object.
 *
 * Fail-loud contract (SASADM-0077 / Phase 7): the origin vars
 * (`VITE_AUTH_BASE_URL`, `VITE_ADMIN_API_BASE_URL`, `VITE_CENTRALIZED_LOGIN_URL`,
 * `VITE_SAAS_ADMIN_ORIGIN`) are REQUIRED in a built bundle and have NO
 * `*.journix.ai` / `window.location.origin` fallback. Vite replaces env at build
 * time, so a mis-set var would otherwise silently ship the wrong origins. Instead:
 *   - In DEV (`import.meta.env.DEV`, i.e. `vite dev` / Vitest) each origin falls
 *     back to a localhost-family default so the app still boots without a `.env`.
 *   - In a built bundle a missing origin resolves to `''` and `validateEnv(env)`
 *     — called at module load only when `import.meta.env.PROD` — THROWS, naming the
 *     offending `VITE_*` var(s). Per-env values live in `.env.production` /
 *     `.env.staging`.
 */
export interface AppEnv {
  authBaseUrl: string;
  adminApiBaseUrl: string;
  centralizedLoginUrl: string;
  centralizedLoginPath: string;
  saasAdminOrigin: string;
  envLabel: string;
  supportedLocales: readonly string[];
  cloudflareAccountId: string;
  /**
   * SASADM-0156 — the brand editor's new image slots (saas-auth SASATH-0347).
   * Off unless `VITE_BRAND_ASSET_SLOTS=true`; `.env.staging` (SASADM-0160) and
   * `.env.production` (Operator switch-on after the staging go-live) set it.
   */
  brandAssetSlots?: boolean;
  /**
   * SASADM-0159 — the brand editor's theme bundle Export / Import (saas-auth
   * SASATH-0351 §3). Off unless `VITE_BRAND_BUNDLE=true`; `.env.staging` (SASADM-0160)
   * and `.env.production` (Operator switch-on after the staging go-live) set it.
   */
  brandBundle?: boolean;
}

/**
 * Resolve a required origin var: explicit value wins; otherwise a localhost-family
 * default in DEV so `vite dev`/Vitest boot without a `.env`, and `''` in a built
 * bundle so `validateEnv` catches the missing var loudly at boot. NEVER a
 * `*.journix.ai` fallback.
 */
function requiredOrigin(value: string | undefined, devFallback: string): string {
  const trimmed = value?.trim();
  if (trimmed) return trimmed;
  return import.meta.env.DEV ? devFallback : '';
}

export const env: AppEnv = {
  authBaseUrl: requiredOrigin(import.meta.env.VITE_AUTH_BASE_URL, 'http://localhost:8080'),
  adminApiBaseUrl: requiredOrigin(
    import.meta.env.VITE_ADMIN_API_BASE_URL,
    'http://localhost:8080',
  ),
  centralizedLoginUrl: requiredOrigin(
    import.meta.env.VITE_CENTRALIZED_LOGIN_URL,
    'http://localhost:5173',
  ),
  centralizedLoginPath: (import.meta.env.VITE_CENTRALIZED_LOGIN_PATH ?? '/login').trim(),
  saasAdminOrigin: requiredOrigin(
    import.meta.env.VITE_SAAS_ADMIN_ORIGIN,
    'http://localhost:5174',
  ),
  envLabel: import.meta.env.VITE_ENV_LABEL?.trim() || 'local',
  supportedLocales: (import.meta.env.VITE_SUPPORTED_LOCALES?.trim() || 'en,es,fr,pt')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0),
  // Optional (like envLabel) — a non-secret Cloudflare account id enabling the
  // tenant Captcha "Manage in Cloudflare" deep link. Empty when unset → link hidden.
  cloudflareAccountId: import.meta.env.VITE_CLOUDFLARE_ACCOUNT_ID?.trim() || '',
  brandAssetSlots: import.meta.env.VITE_BRAND_ASSET_SLOTS === 'true',
  brandBundle: import.meta.env.VITE_BRAND_BUNDLE === 'true',
};

/**
 * Map each required-origin `AppEnv` field to the `VITE_*` var that populates it,
 * so a validation failure names the actionable variable.
 */
const REQUIRED_ORIGIN_VARS: ReadonlyArray<readonly [keyof AppEnv, string]> = [
  ['authBaseUrl', 'VITE_AUTH_BASE_URL'],
  ['adminApiBaseUrl', 'VITE_ADMIN_API_BASE_URL'],
  ['centralizedLoginUrl', 'VITE_CENTRALIZED_LOGIN_URL'],
  ['saasAdminOrigin', 'VITE_SAAS_ADMIN_ORIGIN'],
];

/**
 * Throw a clear, var-naming error when any required origin is empty. Pure and
 * exported so tests can exercise it directly without flipping `import.meta.env`.
 */
export function validateEnv(e: AppEnv): void {
  const missing = REQUIRED_ORIGIN_VARS.filter(([field]) => !String(e[field]).trim()).map(
    ([, varName]) => varName,
  );
  if (missing.length > 0) {
    throw new Error(
      `Missing required environment configuration: ${missing.join(', ')}. ` +
        'A built saas-admin bundle must set every origin var explicitly ' +
        '(see .env.production / .env.staging).',
    );
  }
}

// Fail loud at boot for built bundles only. `vite dev` and Vitest have
// `import.meta.env.PROD === false`, so they never throw at import — tests exercise
// `validateEnv` directly.
if (import.meta.env.PROD) {
  validateEnv(env);
}

/**
 * SASADM-0132 — prefix the browser title with the environment label on any
 * non-production environment, mirroring the `isNonProd` convention that drives the
 * AppBar chip and the non-prod banner. Pure and exported so it is testable without a
 * DOM; the caller owns `document.title`. Returns `baseTitle` unchanged in production,
 * and is idempotent — a title already carrying the prefix is returned as-is, so a
 * double invocation cannot produce `STAGING: STAGING: …`.
 */
export function envPrefixedTitle(baseTitle: string, envLabel: string): string {
  const label = envLabel.trim().toUpperCase();
  if (label === '' || label === 'PRODUCTION') return baseTitle;
  const prefix = `${label}: `;
  return baseTitle.startsWith(prefix) ? baseTitle : `${prefix}${baseTitle}`;
}
